import moment from "moment-timezone";

const conversationProgressItem = (conversation) => ({
  id: conversation.id,
  touchpointId: conversation.id,
  title: conversation.playbook_name || "Humanly conversation",
  status: conversation.status,
  statusAt: conversation.last_message_at,
  qualified_at: conversation.qualified_at,
  inserted_at: conversation.inserted_at,
  touchpointStatus: "reached",
  touchpointType: "Conversation",
  statusAtAgo: moment(conversation.last_message_at).fromNow(),
});

const interviewProgressItem = (interview) => {

  const titlePrefix = () => {
    switch (interview.channel) {
      case "video": {
        return "Video interview";
      }
      case "phone": {
        return "Phone screening";
      }
      case "chat": {
        return "Chat conversation";
      }
      default: {
        return "";
      }
    }
  }

  return {
    id: interview.id,
    touchpointId: interview.id,
    title: `${titlePrefix()} for ${interview.candidate.job.title}`,
    score: interview.score,
    aggregate_score: interview.score,
    summary: interview.summary,
    red_flags: interview.red_flags,
    unresolved_items: interview.unresolved_items,
    rubric_scores: interview.rubric_scores,
    topics: interview.topics,
    statusAt: interview.inserted_at,
    statusAtAgo: moment(interview.inserted_at).fromNow(),
    touchpointStatus: "interview",
    touchpointType: "Interview",
    candidate: interview.candidate,
  };
};

const parseDateString = (date_string) => {
  if (!date_string) return null;
  const date = moment(date_string);
  if (!date.isValid()) return null;
  return { year: date.year(), month: date.month() + 1 };
};

const formatEmployment = (employment) => ({
  role: employment.role,
  name: employment.company,
  description: employment.description,
  startDate: parseDateString(employment.started_at),
  endDate: parseDateString(employment.ended_at),
});

const formatEducation = (education) => {
  const start_date = parseDateString(education.started_at);
  const end_date = parseDateString(education.ended_at);

  return {
    name: education.accreditor,
    degree: education.type,
    fieldOfStudy: education.domain,
    yearStart: start_date?.year,
    yearEnd: end_date?.year,
    endDate: end_date,
  };
};

export const conversationsToTouchpoints = (conversations) =>
  conversations.map((conversation) => {
    const progressItem = conversationProgressItem(conversation);
    return {
      ...progressItem,
      progressItems: [progressItem],
    };
  });

export const interviewsToTouchpoints = (interviews) =>
  interviews.map((interview) => {
    const progressItem = interviewProgressItem(interview);
    return {
      ...progressItem,
      progressItems: [progressItem],
    };
  });

const eventStatusLabel = (status) => {
  switch (status) {
    case "confirmed": return "Going";
    case "pending": return "Pending";
    case "declined": return "Not going";
    case "removed": return "Removed";
    default: return status;
  }
};

const eventRegistrationProgressItem = (event) => ({
  id: event.uuid,
  touchpointId: event.uuid,
  title: event.title || "Event registration",
  statusAt: event.start_time,
  statusAtAgo: moment(event.start_time).fromNow(),
  touchpointStatus: "reached",
  touchpointType: "Event",
  registered_at: event.registered_at,
  start_time: event.start_time,
  end_time: event.end_time,
  time_zone: event.time_zone,
  status: event.status,
  statusLabel: eventStatusLabel(event.status),
});

const meetingProgressItem = (meeting) => ({
  id: meeting.id,
  touchpointId: meeting.id,
  title: "Scheduled meeting",
  statusAt: meeting.starts_at,
  statusAtAgo: moment(meeting.starts_at).fromNow(),
  touchpointStatus: "reached",
  touchpointType: "Meeting",
  uuid: meeting.uuid,
  starts_at: meeting.starts_at,
  ends_at: meeting.ends_at,
  time_zone: meeting.time_zone,
  status: meeting.status,
});

const formatCandidacy = (candidacy) => {
  const summary = candidacy.summaries?.[0];
  return {
    id: candidacy.uuid,
    aggregate_score: candidacy.aggregate_score,
    job: candidacy.job,
    summary: summary?.content || "",
    recommendation: summary?.recommendation || "",
  };
};

const hasFitData = (candidacy) =>
  candidacy.aggregate_score != null || !!candidacy.summary || !!candidacy.recommendation;

export const formatCandidacies = (candidacies = []) =>
  candidacies.map(formatCandidacy).filter(hasFitData);

export const eventsToTouchpoints = (events) =>
  events.map((event) => {
    const progressItem = eventRegistrationProgressItem(event);
    return {
      ...progressItem,
      progressItems: [progressItem],
    };
  });

export const meetingsToTouchpoints = (meetings) =>
  meetings.map((meeting) => {
    const progressItem = meetingProgressItem(meeting);
    return {
      ...progressItem,
      progressItems: [progressItem],
    };
  });

export const formatEmployments = (employments = []) =>
  employments.map(formatEmployment);

export const formatEducations = (educations = []) =>
  educations.map(formatEducation);

export const resumeFileUrl = (file) => file?.url;

export const resumeFileType = (file) => file?.type || null;

const transcriptMessage = (msg) => ({
  authorType: msg.author_type,
  body: msg.body,
});

export const formatTranscriptMessages = (messages = []) =>
  messages.map(transcriptMessage);

/**
 * Transforms Humanly API responses into candidate data format for merging
 * @param {Object} resume - Resume data from Humanly API
 * @param {Array} conversations - Conversations from Humanly API
 * @param {Array} interviews - Interviews from Humanly API
 * @param {Array} events - Event registrations from Humanly API
 * @param {Array} meetings - Meetings from Humanly API
 * @param {Array} candidacies - Candidacies from Humanly API
 * @returns {Object} Transformed candidate data ready for store merge
 */
export const toCandidateData = (resume, conversations, interviews, events = [], meetings = [], candidacies = []) => {
  const employments = formatEmployments(resume?.employments);
  const educations = formatEducations(resume?.educations);
  const resume_file_url = resumeFileUrl(resume?.file);
  const resume_file_type = resumeFileType(resume?.file);

  const hasValue = ([, v]) => Array.isArray(v) ? v.length > 0 : typeof v === "string" && v !== "";

  const optional_fields = Object.fromEntries(
    Object.entries({
      employments,
      educations,
      resumeFileUrl: resume_file_url,
      resumeFileType: resume_file_type,
    }).filter(hasValue)
  );

  return {
    humanlyProfile: {
      resume,
      conversations: conversationsToTouchpoints(conversations),
      interviews: interviewsToTouchpoints(interviews),
      events: eventsToTouchpoints(events),
      meetings: meetingsToTouchpoints(meetings),
      candidacies: formatCandidacies(candidacies),
    },
    ...optional_fields,
  };
};
